/** Scrapers — read-only monitoring for v2 (v1 scraper-health.js). Reads the
 * global scraper_health incidents + fetch_stats (direct vs Scrapfly cost) tables
 * via PostgREST, the SAME queries v1 uses (fetchScraperHealth / fetchFetchStats).
 * PHASE20 adds the SELECT policies. Managing/pausing jobs (scraper_jobs) is a
 * later write-door round; this restores the health + cost visibility first. */
import { http } from "../../lib/http";
import { SUPABASE_URL, SUPABASE_ANON_KEY } from "../../config";

const headers = (token: string): Record<string, string> => ({
  apikey: SUPABASE_ANON_KEY,
  Authorization: `Bearer ${token}`,
});

const str = (v: unknown): string => (typeof v === "string" ? v : v == null ? "" : String(v));
const numOr0 = (v: unknown): number => {
  const n = typeof v === "number" ? v : Number(v);
  return Number.isFinite(n) ? n : 0;
};

export interface ScraperIncident {
  id: string;
  source: string;
  county: string;
  kind: string;
  whereTxt: string;
  whatTxt: string;
  severity: string;
  diagnosis: string;
  retryDone: boolean;
  consecutive: number;
  status: string; // incident | degraded | resolved
  createdAt: string;
}

export function narrowIncident(raw: unknown): ScraperIncident {
  const o = raw && typeof raw === "object" ? (raw as Record<string, unknown>) : {};
  return {
    id: str(o["id"]),
    source: str(o["source"]),
    county: str(o["county"]),
    kind: str(o["kind"]) || "incident",
    whereTxt: str(o["where_txt"]),
    whatTxt: str(o["what_txt"]),
    severity: str(o["severity"]),
    diagnosis: str(o["diagnosis"]),
    retryDone: o["retry_done"] === true,
    consecutive: numOr0(o["consecutive"]),
    status: str(o["status"]) || "incident",
    createdAt: str(o["created_at"]),
  };
}

export async function fetchScraperHealth(token: string, limit = 100, signal?: AbortSignal): Promise<ScraperIncident[]> {
  // A missing token must FAIL, not resolve [] — resolving [] rendered as
  // "✓ All healthy" during every auth-refresh gap (the page's own rows=null
  // lesson, but smuggled in through the loader).
  if (!token) throw new Error("no session token yet");
  const rows = await http<unknown[]>(
    `${SUPABASE_URL}/rest/v1/scraper_health?order=updated_at.desc.nullslast,created_at.desc&limit=${limit}`,
    { headers: headers(token), signal, timeoutMs: 20000 },
  );
  return (Array.isArray(rows) ? rows : []).map(narrowIncident);
}

/** One-click "Fix it" (Tom 2026-08-25): run the safe remediation for an
 * incident and report what it did. Rides the backend door, admin-gated. */
export async function remediateIncident(
  token: string | null,
  inc: { source: string; kind: string; county?: string },
): Promise<{ ok: boolean; did: string[]; needs: string[] }> {
  const { backendFetch } = await import("../../lib/backend");
  return backendFetch(token, "/api/scrapers/remediate", {
    method: "POST",
    body: { source: inc.source, kind: inc.kind, ...(inc.county ? { county: inc.county } : {}) },
    timeoutMs: 60000,
  });
}

export interface FetchStats { direct: number; scrapfly: number; }

export async function fetchFetchStats(token: string, sinceDays = 7, signal?: AbortSignal): Promise<FetchStats> {
  // Tom 2026-08-16: "the health bar still disappears" — THE bug. A token gap
  // (auth refresh race, worst with several CRM tabs open) RESOLVED here as
  // {0,0}; the page then computed totalF=0 and unrendered the whole
  // bandwidth section seconds after it had shown real numbers. Failure must
  // look like failure: throw, and the page keeps the last good stats.
  if (!token) throw new Error("no session token yet");
  const since = new Date(Date.now() - sinceDays * 864e5).toISOString();
  const rows = await http<{ direct?: unknown; scrapfly?: unknown }[]>(
    `${SUPABASE_URL}/rest/v1/fetch_stats?select=direct,scrapfly&created_at=gte.${since}&limit=5000`,
    { headers: headers(token), signal, timeoutMs: 20000 },
  );
  let direct = 0, scrapfly = 0;
  for (const x of Array.isArray(rows) ? rows : []) {
    direct += numOr0(x.direct);
    scrapfly += numOr0(x.scrapfly);
  }
  return { direct, scrapfly };
}

// ============================================================================
// SCRAPER JOBS — the console (Phase 21). Read via the crm2_scraper_jobs_read
// RLS policy; every write goes through the crm2_scraper_job_apply door. This is
// v1's saveScraperJobToDb / toggleScraperJobEnabled / deleteScraperJobFromDb /
// pauseAllScrapersDb, re-expressed as one org-fenced RPC. The `config` shape is
// preserved exactly so jobs created here run identically on the server cron.
// ============================================================================

/** The three buildable job sources (wholesaler is review-only, not a job row). */
export type ScraperSource = "zillow" | "investorlift" | "pricemonitor";
export const JOB_SOURCES: ScraperSource[] = ["zillow", "investorlift", "pricemonitor"];

export interface ScraperJob {
  id: string;
  orgId: string;
  source: ScraperSource;
  name: string;
  config: Record<string, unknown>;
  enabled: boolean;
  status: string; // running | paused | complete | idle (cron-managed)
  lastMessage: string;
  lastRun: string;
  createdAt: string;
}

function asSource(v: unknown): ScraperSource {
  const s = str(v);
  return s === "zillow" || s === "pricemonitor" ? s : "investorlift";
}

/** True only for the three buildable sources (matches the Phase 21 door's
 * source whitelist; the 'wholesaler' pseudo-source is review-only, never a job). */
export function asSourceGuard(v: unknown): v is ScraperSource {
  return v === "zillow" || v === "investorlift" || v === "pricemonitor";
}

export function narrowJob(raw: unknown): ScraperJob {
  const o = raw && typeof raw === "object" ? (raw as Record<string, unknown>) : {};
  const cfg = o["config"];
  return {
    id: str(o["id"]),
    orgId: str(o["org_id"]),
    source: asSource(o["source"]),
    name: str(o["name"]) || "Untitled job",
    config: cfg && typeof cfg === "object" && !Array.isArray(cfg) ? (cfg as Record<string, unknown>) : {},
    enabled: o["enabled"] === true,
    status: str(o["status"]) || "idle",
    lastMessage: str(o["last_message"]),
    lastRun: str(o["last_run"]),
    createdAt: str(o["created_at"]),
  };
}

/** All scraper jobs for this org (RLS-scoped), newest first — grouped by source
 * so the console can show one list per crawler (v1 loadScraperJobs grouping). */
export async function fetchScraperJobs(token: string, signal?: AbortSignal): Promise<Record<ScraperSource, ScraperJob[]>> {
  const grouped: Record<ScraperSource, ScraperJob[]> = { zillow: [], investorlift: [], pricemonitor: [] };
  if (!token) return grouped;
  const rows = await http<unknown[]>(
    `${SUPABASE_URL}/rest/v1/scraper_jobs?select=*&order=created_at.desc`,
    { headers: headers(token), signal, timeoutMs: 20000 },
  );
  for (const r of Array.isArray(rows) ? rows : []) {
    const j = narrowJob(r);
    if (j.source in grouped) grouped[j.source].push(j);
  }
  return grouped;
}

interface JobApplyResult { ok: boolean; act: string; id?: string; enabled?: boolean; updated?: boolean; count?: number; }

async function jobApply(token: string, op: Record<string, unknown>): Promise<JobApplyResult> {
  const r = await http<JobApplyResult>(`${SUPABASE_URL}/rest/v1/rpc/crm2_scraper_job_apply`, {
    method: "POST",
    headers: headers(token),
    body: { op },
    attempts: 1,
    timeoutMs: 20000,
  });
  return r ?? { ok: false, act: op["act"] as string };
}

/** Insert (no id) or update (id) a job. Returns the row id (new on insert). */
export async function saveScraperJob(
  token: string,
  job: { id?: string; source: ScraperSource; name: string; config: Record<string, unknown>; enabled: boolean },
): Promise<string> {
  const op: Record<string, unknown> = { act: "save", source: job.source, name: job.name, config: job.config, enabled: job.enabled };
  if (job.id) op["id"] = job.id;
  const r = await jobApply(token, op);
  return r.id ?? job.id ?? "";
}

export async function toggleScraperJob(token: string, id: string, enabled: boolean): Promise<void> {
  await jobApply(token, { act: "toggle", id, enabled });
}

export async function deleteScraperJob(token: string, id: string): Promise<void> {
  await jobApply(token, { act: "delete", id });
}

/** Org-wide master switch: enabled=false pauses every job (v1 pauseAllScrapersDb). */
export async function pauseAllScrapers(token: string, enabled: boolean): Promise<number> {
  const r = await jobApply(token, { act: "pauseall", enabled });
  return r.count ?? 0;
}

// ---- config path helpers (v1 scraper-builder jv/setp) — read/write nested keys
// on a plain object by dotted path, so the React form stays wire-compatible with
// the server runner's expected config shape. -----------------------------------

export function cfgGet(cfg: Record<string, unknown>, path: string, def: unknown): unknown {
  const parts = path.split(".");
  let o: unknown = cfg;
  for (const p of parts) {
    if (o == null || typeof o !== "object") return def;
    o = (o as Record<string, unknown>)[p];
  }
  return o == null || o === "" ? def : o;
}

/** Immutably set a nested value by dotted path (returns a new config object). */
export function cfgSet(cfg: Record<string, unknown>, path: string, val: unknown): Record<string, unknown> {
  const parts = path.split(".");
  const root: Record<string, unknown> = { ...cfg };
  let o = root;
  for (let i = 0; i < parts.length - 1; i++) {
    const key = parts[i]!;
    const cur = o[key];
    const next: Record<string, unknown> = cur && typeof cur === "object" && !Array.isArray(cur) ? { ...(cur as Record<string, unknown>) } : {};
    o[key] = next;
    o = next;
  }
  o[parts[parts.length - 1]!] = val;
  return root;
}

/** CSV convenience: read a nested array/string as a comma-joined string. */
export function cfgCsv(cfg: Record<string, unknown>, path: string): string {
  const v = cfgGet(cfg, path, []);
  if (Array.isArray(v)) return v.join(", ");
  return typeof v === "string" ? v : "";
}

/** Parse a CSV input back to a trimmed non-empty string array (for cfgSet). */
export function csvParse(s: string): string[] {
  return String(s).split(",").map((x) => x.trim()).filter(Boolean);
}

/** v1 _jobToUi chips/interval: compact human summary for a job card. */
export function jobInterval(cfg: Record<string, unknown>): string {
  const sch = (cfg["schedule"] as Record<string, unknown>) || {};
  if (sch["repeat"] === false) return "one-shot";
  const every = cfgGet(cfg, "schedule.every", "");
  const unit = String(cfgGet(cfg, "schedule.unit", "Minutes")).toLowerCase();
  if (every === "" || every == null) return "—";
  return `every ${every}${unit.startsWith("hour") ? "h" : "m"}`;
}

export function jobStatusHue(status: string): string {
  if (status === "running") return "emerald";
  if (status === "paused") return "amber";
  if (status === "complete" || status === "resolved") return "slate";
  return "slate";
}

/** v1 _healthPill kind→hue + open/degraded semantics. */
export const isOpen = (i: ScraperIncident): boolean => i.status !== "resolved";
export function incidentHue(i: ScraperIncident): string {
  if (i.status === "resolved") return "emerald";
  if (i.status === "degraded") return "red";
  const byKind: Record<string, string> = {
    blocked: "amber", timeout: "amber", portal_down: "orange", crash: "red", zero_results: "slate", silent_parse_failure: "red",
  };
  return byKind[i.kind] ?? "slate";
}
